import styled from 'styled-components'

import { DefectDueDate } from './DefectDueDate'
import { DefectPriority } from './DefectPriority'
import { DefectStatusTag } from './DefectStatusTag'
import { getDefectName } from './getDefectName'

import {
  CorePageViewEntity,
  NarrowContent,
} from '@/components/layout-components/CorePageViewEntity'
import { AssigneePill } from '@/components/shared-components/AssigneePill'
import { AttachmentBoxes } from '@/components/shared-components/AttachmentBox'
import { ConversationThread } from '@/components/shared-components/ConversationThread'
import { getAssigneeName, getLocationHierarchy } from '@/utils/index'

import { AuditLogLike, DefectLike, KnockMessageLogLike } from 'types/vagueTypes'

const ProjectAndLocation = styled.span`
  display: block;
  color: ${({ theme }) => theme.colour.secondary._500};
  margin-bottom: 1rem;
`

const Assignees = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`

const AttachmentsWrapper = styled(NarrowContent)`
  margin-bottom: 2.5rem;
`

type Props = {
  defect: DefectLike
  auditLogs?: AuditLogLike[]
  messageLogs?: KnockMessageLogLike[]
  actions?: React.ReactNode
  beforeContent?: React.ReactNode
  children?: React.ReactNode
  topOfScrollContent?: React.ReactNode
}

export const ViewDefectEntity: React.FC<Props> = ({
  defect,
  auditLogs,
  messageLogs,
  actions,
  beforeContent,
  children,
  topOfScrollContent,
}) => {
  const [name, type] = getDefectName(defect)

  const locations = defect?.location
    ? getLocationHierarchy(defect.location)
        .reverse()
        .map((l) => l.name)
    : []

  return (
    <CorePageViewEntity
      loading={!defect?.id}
      title={name}
      description={defect?.description}
      actions={actions}
      beforeContent={beforeContent}
      infoLeft={
        <>
          {type} #{defect?.order}
          <DefectPriority priority={defect?.priority} />
        </>
      }
      infoRight={
        <>
          <DefectStatusTag status={defect?.status} />
          <DefectDueDate dueDate={defect?.dueDate} overdue={defect?.overdue} />
        </>
      }
      extraDetails={
        <>
          <ProjectAndLocation>
            {defect?.project.name}
            {locations.length > 0 && ` | ${locations.join(' > ')}`}
          </ProjectAndLocation>
          <Assignees>
            {defect?.assignees
              ?.map((a) => getAssigneeName(a))
              .map(([name, type, id]) => (
                <AssigneePill key={id} id={id} name={name} type={type} />
              ))}
          </Assignees>
        </>
      }
    >
      {!!defect?.attachments?.length && (
        <AttachmentsWrapper>
          <AttachmentBoxes
            width={144}
            height={113}
            attachments={defect.attachments.map((a) => ({
              url: a.thumbnail?.small || a.url,
              previewUrl: a.url,
              alt: a.fileName,
            }))}
          />
        </AttachmentsWrapper>
      )}

      <NarrowContent column>
        <ConversationThread
          conversation={defect?.conversation}
          auditLogs={auditLogs}
          messageLogs={messageLogs}
          topOfScrollContent={topOfScrollContent}
        />
      </NarrowContent>

      {children}
    </CorePageViewEntity>
  )
}
